import React, { useState, useEffect, useRef, Suspense, lazy, useMemo } from "react";
import "./App.css";
import { BrowserRouter } from "react-router-dom";
import axios from "axios";
import tokenStore from "./utils/tokenStore";
import { TimerProvider } from "./context/TimerContext";
import { ScheduleProvider } from "./context/ScheduleContext";
import { IssuesProvider } from "./context/IssuesContext";
import { TeamProvider } from "./context/TeamContext";
import { ThemeProvider } from "./context/ThemeContext";
import { ToastProvider } from "./context/ToastContext";
import { ToolHistoryProvider } from "./context/ToolHistoryContext";
import socketService from "./services/SocketService";
import { initSentry, initGA4, trackPageView, trackLogin, setUserProperties, trackError } from "./utils/monitoring";
import { installErrorBuffer } from "./utils/errorBuffer";
import CollapsibleSidebar from "./components/layout/CollapsibleSidebar";
import SidebarSettings from "./components/layout/SidebarSettings";
import MobileBottomNav from "./components/layout/MobileBottomNav";
import GlobalFloatingNotes from "./components/widgets/GlobalFloatingNotes";
import GlobalNotificationListener from "./components/GlobalNotificationListener";
// GlobalFloatingTimer removed — timer state lives in TimerProvider; controls now appear inline on Today.
import FeedbackWidget from "./components/feedback/FeedbackWidget";
import useIsMobile from "./hooks/useIsMobile";
import { detectAndSaveCurrency } from "./utils/currencyDetect";
import { logEvent } from "./utils/trackEvent";
import useSwipeNavigation from "./hooks/useSwipeNavigation";
import { ROUTES, DEFAULT_ROUTE, ROUTE_LABELS } from "./constants/routes";
import { API_URL } from "./constants/api";
import PricingPage from "./components/billing/PricingPage";
import LockedFeature from "./components/billing/LockedFeature";
import TermsOfService from "./components/legal/TermsOfService";
import PrivacyPolicy from "./components/legal/PrivacyPolicy";
import ConsentBanner from "./components/ConsentBanner";
import { startAnalytics, syncConsentToAccount } from "./utils/analytics";
import { showScheduleDay } from "./utils/scheduleDay";
import Disclaimer from "./components/legal/Disclaimer";
import GetStarted from "./components/legal/GetStarted";
import LoginForm from "./components/auth/LoginForm";
import RegisterForm from "./components/auth/RegisterForm";
import ForgotPasswordForm from "./components/auth/ForgotPasswordForm";
import ResetPasswordForm from "./components/auth/ResetPasswordForm";
import LandingPage from "./components/landing/LandingPage";

const isDev = process.env.NODE_ENV === 'development';
const devLog = (...args) => isDev && console.log(...args);

// Lazy-loaded components (load on-demand)
const PreviewDashboard = lazy(() => import("./components/PreviewDashboard"));
const PreviewBuildTab = lazy(() => import("./components/PreviewBuildTab"));
const PreviewCompanyTab = lazy(() => import("./components/PreviewCompanyTab"));
const PreviewTeamHub = lazy(() => import("./components/PreviewTeamHub"));
const AICoFounderAssistant = lazy(() => import("./components/AICoFounderAssistant"));
const AdminDashboard = lazy(() => import("./components/AdminDashboard"));
import OnboardingWizard from "./components/OnboardingWizard";
import DemoModeTour from "./components/DemoModeTour";

// Loading Fallback Component
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen" style={{ backgroundColor: 'var(--bg-base)' }}>
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto mb-4"></div>
      <p style={{ color: 'var(--text-muted)' }}>Loading...</p>
    </div>
  </div>
);

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
    // Track error in monitoring systems
    trackError(error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-warm-charcoal flex items-center justify-center p-6">
          <div className="card-calming p-8 max-w-lg w-full">
            <div className="text-center mb-6">
              <div className="text-6xl mb-4">⚠️</div>
              <h2 className="text-xl font-bold text-primary-text heading-calming mb-2">
                Something went wrong
              </h2>
              <p className="text-calming">
                We encountered an unexpected error. Don't worry, your data is safe.
              </p>
            </div>
            
            <div className="space-y-4">
              <button
                onClick={() => window.location.reload()}
                className="w-full btn-calming-primary"
              >
                🔄 Reload App
              </button>
              
              <button
                onClick={() => {
                  localStorage.clear();
                  window.location.href = '/';
                }}
                className="w-full btn-calming-secondary"
              >
                🔧 Reset & Restart
              </button>
            </div>
            
            {process.env.NODE_ENV === 'development' && (
              <details className="mt-6">
                <summary className="text-sm text-muted-calming cursor-pointer hover:text-primary-text">
                  🔍 Developer Info
                </summary>
                <div className="mt-3 p-3 bg-light-charcoal rounded text-xs font-mono text-calming">
                  <div className="mb-2">
                    <strong>Error:</strong> {this.state.error && this.state.error.toString()}
                  </div>
                  <div>
                    <strong>Stack:</strong>
                    <pre className="mt-1 whitespace-pre-wrap">
                      {this.state.errorInfo && this.state.errorInfo.componentStack}
                    </pre>
                  </div>
                </div>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const API = `${API_URL}/api`;

// Configure axios defaults
axios.defaults.timeout = 60000; // 60 seconds - increased for better reliability
axios.defaults.headers.common['Content-Type'] = 'application/json';
axios.defaults.withCredentials = true; // IMPORTANT: credentials=true to send cookies with requests

// Set up axios interceptor for auth
axios.interceptors.request.use((config) => {
  const token = tokenStore.get();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  devLog("Making request to:", config.url);
  return config;
});

// Global response interceptor for handling 401 and token refresh
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  isRefreshing = false;
  failedQueue = [];
};

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const requestUrl = originalRequest?.url || '';

    // NEVER intercept auth endpoints — they handle their own errors
    // This prevents infinite refresh loops (refresh 401 → interceptor tries refresh → 401 → ...)
    const isAuthEndpoint = requestUrl.includes('/auth/refresh') || 
                           requestUrl.includes('/auth/login') || 
                           requestUrl.includes('/auth/register');

    // Skip handling for aborted/canceled requests (e.g. component unmount)
    if (axios.isCancel(error) || error.code === 'ERR_CANCELED' || originalRequest?.signal?.aborted) {
      return Promise.reject(error);
    }

    // Handle 401 Unauthorized - attempt token refresh (but NOT for auth endpoints)
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return axios(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        devLog('[Interceptor] 401 on', requestUrl, '— attempting token refresh...');
        
        // Attempt 1: Refresh token using httpOnly cookie (preferred for security)
        let refreshResponse;
        try {
          refreshResponse = await axios.post(
            `${API}/auth/refresh`,
            {},
            { withCredentials: true }
          );
        } catch (cookieRefreshError) {
          devLog('[Interceptor] Cookie-based refresh failed:', cookieRefreshError.response?.status);
          
          // Attempt 2: Fallback - try refresh with stored refresh_token (handles cookie
          // blocking by Safari ITP / Brave / third-party-cookie restrictions, AND any
          // non-401 cookie failure such as network blips after tab wake-up).
          const fallbackRefreshToken = tokenStore.getRefreshToken();
          if (fallbackRefreshToken) {
            devLog('[Interceptor] Trying fallback refresh with stored token...');
            try {
              refreshResponse = await axios.post(
                `${API}/auth/refresh`,
                { refresh_token: fallbackRefreshToken },
                { withCredentials: true }
              );
              devLog('[Interceptor] ✓ Fallback refresh successful');
            } catch (bodyRefreshError) {
              console.error('[Interceptor] Fallback refresh also failed:', bodyRefreshError.response?.status);
              throw bodyRefreshError;
            }
          } else {
            throw cookieRefreshError;
          }
        }

        const newAccessToken = refreshResponse.data.access_token;
        tokenStore.set(newAccessToken);
        
        // Store new refresh token if provided
        if (refreshResponse.data.refresh_token) {
          tokenStore.setRefreshToken(refreshResponse.data.refresh_token);
        }
        
        // Retry the original request with new token
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        processQueue(null, newAccessToken);
        return axios(originalRequest);
      } catch (refreshError) {
        // Refresh failed - clear auth state (do NOT force reload — let React handle it)
        console.error('[Interceptor] Token refresh failed completely, clearing auth');
        tokenStore.clear();
        tokenStore.clearRefreshToken();
        processQueue(refreshError, null);
        
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

const App = () => {
  // Detect mobile viewport to hide floating widgets
  const isMobile = useIsMobile(768);

  // Initialize monitoring systems on app start
  // GA4 is gated behind cookie consent (ePrivacy Directive + Dutch Telecom Act).
  // Sentry is legitimate-interest and fires regardless (no user PII captured).
  useEffect(() => {
    installErrorBuffer();
    initSentry();
    // The consent banner was removed, so nothing sets this key any more. Visitors
    // who accepted while the banner existed keep their stored 'accepted' and still
    // get GA4; everyone else runs without analytics cookies.
    if (localStorage.getItem('cookieConsent') === 'accepted') initGA4();
  }, []);

  // Feature flag for sidebar (can toggle back to top nav easily)
  const [useSidebar, setUseSidebar] = useState(() => {
    const savedPref = localStorage.getItem('useSidebarNav');
    return savedPref !== 'false'; // Default to true (sidebar enabled)
  });

  // State variables - Initialize authentication state from localStorage
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    const token = tokenStore.get();
    return !!token; // Convert to boolean
  });
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authStage, setAuthStage] = useState('authenticating'); // For enhanced loading states
  const [networkError, setNetworkError] = useState(false);
  // Logged-out visitors land on the public LandingPage; the auth forms only
  // appear once they ask for them, or when they arrive on an auth URL directly
  // (/login, /signup, a password-reset link, an emailed invite).
  const [showAuth, setShowAuth] = useState(() => {
    const path = window.location.pathname;
    if (path === "/login" || path === "/signup" || path === "/reset-password" || path === "/forgot-password") return true;
    const params = new URLSearchParams(window.location.search);
    return params.has("token") || params.has("invite");
  });
  const [showRegister, setShowRegister] = useState(
    () => window.location.pathname === "/signup"
  );

  // "/" is the public homepage for EVERYONE, signed in or not — a returning
  // user sees it and clicks through to their dashboard. Any other path
  // (/today, /build, …) goes straight into the app, so bookmarks and the
  // post-login redirect skip the marketing page.
  const [enteredApp, setEnteredApp] = useState(
    () => window.location.pathname !== "/"
  );
  const [showForgotPassword, setShowForgotPassword] = useState(
    () => window.location.pathname === "/forgot-password"
  );
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetToken, setResetToken] = useState("");
  const [legalPage, setLegalPage] = useState(null); // 'terms' | 'privacy' | 'disclaimer' | 'getstarted' | null

  // PostHog only runs after "Allow" on the consent banner (utils/analytics.js).
  // When it does, tie it to the signed-in founder's account id.
  useEffect(() => {
    startAnalytics(currentUser || null);
    if (currentUser) syncConsentToAccount(currentUser);
  }, [currentUser?.id]); // eslint-disable-line
  const [showPricing, setShowPricing] = useState(false);
  // Banner shown after returning from Stripe Checkout: { type, text } | null
  const [checkoutNotice, setCheckoutNotice] = useState(null);
  const [activeTab, setActiveTab] = useState(() => {
    // Mobile users land on Today; desktop keeps default
    if (window.innerWidth < 768) return ROUTES.TODAY;
    return DEFAULT_ROUTE;
  });
  const [showWelcome, setShowWelcome] = useState(false); // Skip welcome screen for better UX
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [dashboardKey, setDashboardKey] = useState(0);
  const [showDemoTour, setShowDemoTour] = useState(false); // Read-only product tour overlay
  const [pendingIssueId, setPendingIssueId] = useState(null); // Issue to open in Build Tracker
  const [pendingAiPrompt, setPendingAiPrompt] = useState(null); // Prompt to auto-send in the AI tab
  const [pendingBuildView, setPendingBuildView] = useState(null); // Subtab to open in Build Tracker (e.g. 'milestones')
  const [pendingIssueDraft, setPendingIssueDraft] = useState(null); // Pre-filled new-issue form ({ title, todoId }) — saved only on user confirm
  const [showSettings, setShowSettings] = useState(false); // Settings panel visibility (shared with sidebar + bottom nav)
  const [aiInputFocused, setAiInputFocused] = useState(false); // Track AI input focus to hide nav on keyboard

  // Expose a global opener so any component (e.g. the dashboard "Demo Mode" button,
  // onboarding wizard) can launch the read-only product tour without prop drilling.
  useEffect(() => {
    window.__openDemoTour = () => setShowDemoTour(true);
    return () => { try { delete window.__openDemoTour; } catch (_) {} };
  }, []);

  // Landing ⇄ auth navigation. Pushing a history entry means the browser Back
  // button returns to the landing page instead of leaving the site entirely,
  // and makes /login and /signup real, shareable URLs.
  const openAuth = (register = false) => {
    setShowRegister(register);
    setShowAuth(true);
    window.history.pushState({ fpAuth: true }, "", register ? "/signup" : "/login");
  };

  // "Forgot password?" gets its own history entry. Without one the browser Back
  // button skipped straight past it to "/", leaving the reset form on screen
  // under a homepage URL.
  const openForgotPassword = () => {
    setShowForgotPassword(true);
    window.history.pushState({ fpAuth: true, fpForgot: true }, "", "/forgot-password");
  };

  // Leaving the reset form via its own "Back" button. Rewinding history rather
  // than just flipping state keeps the URL and the screen in step. The
  // replaceState branch covers landing on /forgot-password directly (pasted
  // link, new tab), where there is no entry of ours to rewind to.
  const closeForgotPassword = () => {
    setShowRegister(false);
    if (window.history.state?.fpForgot) {
      window.history.back(); // onPop clears showForgotPassword
      return;
    }
    setShowForgotPassword(false);
    setShowAuth(true);
    window.history.replaceState({ fpAuth: true }, "", "/login");
  };

  // Already signed in and standing on the homepage — go straight to the
  // dashboard, no login form in between.
  const enterApp = () => {
    setEnteredApp(true);
    window.history.pushState({}, "", `/${activeTab || DEFAULT_ROUTE}`);
  };

  useEffect(() => {
    const onPop = () => {
      const path = window.location.pathname;
      setShowAuth(
        path === "/login" || path === "/signup" ||
        path === "/reset-password" || path === "/forgot-password"
      );
      setShowRegister(path === "/signup");
      setShowForgotPassword(path === "/forgot-password");
      setEnteredApp(path !== "/");
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // Allow non-auth components anywhere in the tree to trigger legal pages
  // via a custom event, avoiding prop drilling through the tree.
  // Same pattern for the pricing page, so anything deep in the tree (e.g. the
  // "See plans" button on an AI daily-limit message) can open it without
  // prop-drilling.
  useEffect(() => {
    const handler = () => setShowPricing(true);
    window.addEventListener("fp:showPricing", handler);
    return () => window.removeEventListener("fp:showPricing", handler);
  }, []);

  useEffect(() => {
    const handler = (e) => setLegalPage(e.detail || "privacy");
    window.addEventListener("fp:showLegal", handler);
    return () => window.removeEventListener("fp:showLegal", handler);
  }, []);
  
  // Handle opening a specific issue in Build Tracker
  // The brief goes stale when the founder applies an AI answer to an issue, or
  // links a spun-off issue to it. That happens on the AI tab — and because
  // activeTabContent is a switch, PreviewDashboard is UNMOUNTED at that moment,
  // so a listener living inside it would never hear the event. Bumping the key
  // here means Today remounts and refetches whenever it is next shown.
  useEffect(() => {
    const handler = () => setDashboardKey(k => k + 1);
    window.addEventListener('fp:briefStale', handler);
    return () => window.removeEventListener('fp:briefStale', handler);
  }, []);

  const handleOpenIssue = (issueId) => {
    setPendingIssueId(issueId);
    setActiveTab(ROUTES.BUILD);
  };

  // Handle asking the AI tab a prompt (auto-sends it on arrival via pendingPrompt)
  //
  // `issueId` is passed by Today's brief nudges, which are usually about one
  // specific issue. It rides along as an *anchor*, not as a doc-solve: the brief
  // writes the prompt itself and that wording is the point, so the AI tab must
  // send it verbatim and merely remember which issue it concerned. That memory
  // is what later lets the founder apply an answer back to the issue — a step
  // that stays theirs to take, so the issue Today is reporting on cannot change
  // underneath them just because they read an answer.
  const handleAskAI = (message, focusArea = 'general', issueId = null) => {
    setPendingAiPrompt({ message, focusArea, anchorIssueId: issueId || null, ts: Date.now() });
    setActiveTab(ROUTES.AI);
  };

  // Handle "Ask AI" on a Build issue card — jumps to the AI tab and auto-generates
  // a doc-aware solution for that issue, which the user can then keep chatting about.
  const handleSolveIssueWithAI = (issue) => {
    if (!issue?.id) return;
    const title = issue.title || 'this issue';
    setPendingAiPrompt({
      issueId: issue.id,
      issueTitle: title,
      message: `💡 Help me solve this build issue: "${title}"`,
      focusArea: 'technical',
      ts: Date.now(),
    });
    setActiveTab(ROUTES.AI);
  };

  // Handle opening a specific Build subtab (e.g. Milestones) directly
  const handleOpenBuildView = (view) => {
    setPendingBuildView(view);
    setActiveTab(ROUTES.BUILD);
  };

  // Where each record the AI created actually lives, so a finished chip in the
  // AI tab can jump straight to it. The routing sits here rather than in the
  // chat component because this is the file that knows the tabs.
  const handleOpenAICreated = (type, entityId, payload) => {
    switch (type) {
      case 'create_issue':
        // The only one with its own card — open it directly.
        if (entityId) handleOpenIssue(entityId);
        else setActiveTab(ROUTES.BUILD);
        break;
      case 'update_milestone':
        handleOpenBuildView('milestones');
        break;
      case 'create_todo':
        setActiveTab(ROUTES.TODAY);
        break;
      case 'create_time_block':
        // The schedule is a Today widget — open it on the block's own day.
        if (payload?.date) showScheduleDay(payload.date);
        setActiveTab(ROUTES.TODAY);
        break;
      case 'create_note':
        // Notes are a floating editor, not a tab. `notesUpdated` makes it
        // re-fetch so the new note is there when the panel opens.
        window.dispatchEvent(new Event('notesUpdated'));
        window.dispatchEvent(new Event('openFloatingNotes'));
        break;
      default:
        break;
    }
  };

  // The other chip under an AI reply: a nav chip, which created nothing and so
  // has no record to open. It just goes to the page where that kind of work
  // actually happens — Money for a spend task, Team for a delegation. The
  // server picks the destination (NAV_TARGETS in services/ai_actions.py); this
  // only knows how to reach the tabs.
  const handleAINavigate = (nav) => {
    const tab = nav?.tab;
    // An unrecognised tab does nothing rather than blanking the screen — a
    // dead-end chip would cost more trust than a chip that never appeared.
    if (!tab || !Object.values(ROUTES).includes(tab)) return;
    if (tab === ROUTES.BUILD && nav.view) handleOpenBuildView(nav.view);
    else setActiveTab(tab);
  };

  // Open the new-issue form pre-filled (from a Today to-do). The issue is only
  // created when the user hits Save — cancelling keeps the to-do untouched.
  const handleDraftIssue = (draft) => {
    setPendingIssueDraft(draft);
    setActiveTab(ROUTES.BUILD);
  };
  
  // Track tab changes
  useEffect(() => {
    if (activeTab && isAuthenticated) {
      trackPageView(`/${activeTab}`, ROUTE_LABELS[activeTab] || activeTab);
      logEvent('page_view', { tab: activeTab });
    }
  }, [activeTab, isAuthenticated]);


  // Authentication - validate existing session on mount
  useEffect(() => {
    const setupAuth = async () => {
      try {
        // --- Password-reset deep-link: /reset-password?token=xxx ---
        const params = new URLSearchParams(window.location.search);
        const urlResetToken = params.get("token");
        if (urlResetToken && (window.location.pathname === "/reset-password" || params.has("token"))) {
          setResetToken(urlResetToken);
          setShowResetPassword(true);
          setShowForgotPassword(false);
          window.history.replaceState({}, document.title, window.location.pathname.replace("/reset-password", "/") || "/");
          setLoading(false);
          return;
        }

        // --- Google redirect (iOS) session handoff ---
        // After a successful Google sign-in via redirect mode (used on iOS/Safari
        // because ITP blocks the popup), the backend embeds the refresh token in
        // the URL hash as #google_session=<token>. Read it once, store it so the
        // Attempt-2 fallback below can use it, then wipe the hash from the URL.
        const hashParams = new URLSearchParams(window.location.hash.slice(1));
        const googleSessionToken = hashParams.get('google_session');
        if (googleSessionToken) {
          tokenStore.setRefreshToken(googleSessionToken);
          window.history.replaceState({}, '', window.location.pathname + window.location.search);
        }

        // --- Session restore via httpOnly refresh-token cookie ---
        // On every page load, silently try to get a fresh access token using
        // the httpOnly cookie. This means page refresh never forces re-login.
        let hasValidSession = false;
        
        try {
          devLog('[Auth] Attempting session refresh on page load...');
          let refreshResponse;
          
          // Attempt 1: Use httpOnly cookie (preferred for security)
          try {
            refreshResponse = await axios.post(
              `${API}/auth/refresh`,
              {},
              { withCredentials: true }
            );
            devLog('[Auth] ✓ Refresh successful via httpOnly cookie');
          } catch (cookieRefreshError) {
            if (cookieRefreshError.response?.status === 401) {
              devLog('[Auth] Cookie-based refresh failed (401), trying fallback...');
              
              // Attempt 2: Fallback - use stored refresh_token (cross-origin / cookie-blocked clients)
              const fallbackRefreshToken = tokenStore.getRefreshToken();
              if (fallbackRefreshToken) {
                try {
                  refreshResponse = await axios.post(
                    `${API}/auth/refresh`,
                    { refresh_token: fallbackRefreshToken },
                    { withCredentials: true }
                  );
                  devLog('[Auth] ✓ Refresh successful via fallback refresh token');
                } catch (bodyRefreshError) {
                  console.error('[Auth] Fallback refresh also failed (401)');
                  throw bodyRefreshError;
                }
              } else {
                devLog('[Auth] No fallback refresh token available');
                throw cookieRefreshError;
              }
            } else {
              throw cookieRefreshError;
            }
          }
          
          const newAccessToken = refreshResponse.data.access_token;
          tokenStore.set(newAccessToken);
          
          // Store refresh_token if provided
          if (refreshResponse.data.refresh_token) {
            tokenStore.setRefreshToken(refreshResponse.data.refresh_token);
          }

          // Fetch full user profile with the fresh token
          const meResponse = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${newAccessToken}` }
          });

          if (meResponse.data) {
            setCurrentUser(meResponse.data);
            setIsAuthenticated(true);
            setShowWelcome(localStorage.getItem('welcomeSeen') !== 'true');
            if (!meResponse.data.onboarding_completed) {
              setShowOnboarding(true);
            }
            hasValidSession = true;
            // Connect Socket.IO with the fresh token
            setTimeout(() => {
              socketService.disconnect();
              socketService.connect(newAccessToken);
            }, 1000);
          }
        } catch (refreshError) {
          // Refresh token endpoint failed or doesn't exist yet
          // Try to use the stored access token directly if it exists
          const storedToken = tokenStore.get();
          if (storedToken) {
            try {
              devLog('[Auth] Refresh failed, trying stored access token...');
              const meResponse = await axios.get(`${API}/auth/me`, {
                headers: { Authorization: `Bearer ${storedToken}` }
              });
              
              if (meResponse.data) {
                setCurrentUser(meResponse.data);
                setIsAuthenticated(true);
                setShowWelcome(localStorage.getItem('welcomeSeen') !== 'true');
                if (!meResponse.data.onboarding_completed) {
                  setShowOnboarding(true);
                }
                hasValidSession = true;
                // Connect Socket.IO with the stored token
                setTimeout(() => {
                  socketService.disconnect();
                  socketService.connect(storedToken);
                }, 1000);
              }
            } catch (meError) {
              // Stored token is invalid — log out
              devLog('[Auth] Stored token is invalid, logging out');
              tokenStore.clear();
              tokenStore.clearRefreshToken();
              setIsAuthenticated(false);
              setCurrentUser(null);
            }
          } else {
            // No stored token and refresh failed — show login
            devLog('[Auth] No stored token available, showing login screen');
            setIsAuthenticated(false);
            setCurrentUser(null);
          }
        }
      } catch (unexpectedError) {
        // Safety net: if anything unexpected throws, still show login screen
        console.error('[Auth] Unexpected error during auth setup:', unexpectedError);
        setIsAuthenticated(false);
        setCurrentUser(null);
      } finally {
        // ALWAYS clear loading — no matter what happens above
        setLoading(false);
      }
    };
    
    setupAuth();
  }, []);

  // Deep-link: read ?tab= or /pathname from URL once auth is established
  // Handles both ?tab=today (new emails) and /today (old email links)
  useEffect(() => {
    if (!isAuthenticated) return;
    const params = new URLSearchParams(window.location.search);
    const validTabs = Object.values(ROUTES);

    // Priority 1: ?tab=today (query param style)
    let targetTab = params.get('tab');

    // Priority 2: /today or /build etc. (path style — used in old email links)
    if (!targetTab) {
      const pathTab = window.location.pathname.replace(/^\//, '').split('/')[0];
      if (pathTab && validTabs.includes(pathTab)) {
        targetTab = pathTab;
      }
    }

    const settingsParam = params.get('settings');

    if (targetTab && validTabs.includes(targetTab)) {
      setActiveTab(targetTab);
    }
    if (settingsParam) {
      setShowSettings(true);
    }
    // Clean the URL — replace /today or ?tab=today with just /
    if (targetTab || settingsParam) {
      window.history.replaceState({}, document.title, '/');
    }
  }, [isAuthenticated]); // run once after auth is established

  // Keep the stored timezone in sync with the device, so the 8am daily brief
  // actually lands at the user's 8am. Detection already existed, but only ran
  // inside the Settings panel — users who never opened Settings kept the
  // "UTC" default and got mailed at 8am UTC (1:30pm IST). This runs on every
  // authenticated load and self-heals those users on their next visit, with no
  // backfill script and no IP-geolocation service: Intl reads the real IANA
  // zone straight from the OS, which beats guessing from an IP behind a VPN
  // or a carrier NAT.
  const tzSyncAttempted = useRef(false);
  useEffect(() => {
    if (!isAuthenticated || !currentUser) return;
    // currentUser changes identity on every refetch. Attempt the write once per
    // session so a failing PUT cannot retry on every one of those changes.
    if (tzSyncAttempted.current) return;

    let browserTz;
    try {
      browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return; // no Intl support — leave whatever is stored alone
    }
    if (!browserTz) return;

    const prefs = currentUser.email_preferences || {};
    if (prefs.timezone === browserTz) return; // already correct, no write

    const token = tokenStore.get();
    if (!token) return;

    tzSyncAttempted.current = true;
    axios
      .put(
        `${API}/users/me/profile`,
        {
          // No send_hour_local here: the server merges email_preferences, so the
          // brief hour / onboarding work-time answer are never reset by this sync.
          email_preferences: { daily_brief: prefs.daily_brief !== false, timezone: browserTz },
        },
        { headers: { Authorization: `Bearer ${token}` } },
      )
      .then(() => {
        // Update in place so this effect does not fire again this session.
        setCurrentUser((prev) =>
          prev
            ? {
                ...prev,
                email_preferences: { ...prefs, timezone: browserTz },
              }
            : prev,
        );
      })
      .catch(() => {
        // Non-critical: the brief still sends, just on the old timezone.
        // Retried on the next load.
      });
  }, [isAuthenticated, currentUser]);

  // Return from Stripe Checkout. success_url / cancel_url send the user back to
  // "/?checkout=success|cancelled" — which is the public marketing homepage, so
  // without this they'd land there with no confirmation and a stale plan on
  // currentUser. Pull the fresh plan off /auth/me (the webhook has usually
  // landed by now; the retry covers the case where it hasn't) and drop them
  // back into the app.
  useEffect(() => {
    if (!isAuthenticated) return;
    const params = new URLSearchParams(window.location.search);
    const result = params.get('checkout');
    if (!result) return;

    // Strip the param immediately so a refresh doesn't replay this.
    params.delete('checkout');
    const rest = params.toString();
    window.history.replaceState({}, document.title, rest ? `/?${rest}` : '/');

    if (result !== 'success') {
      setCheckoutNotice({ type: 'info', text: 'Checkout cancelled — you have not been charged.' });
      return;
    }

    setEnteredApp(true);
    setCheckoutNotice({ type: 'success', text: 'Payment received — confirming your plan…' });

    let cancelled = false;
    const confirmPlan = async () => {
      // Stripe redirects the browser back the instant payment clears, which can
      // beat its own webhook by a second or two. Poll briefly rather than
      // showing the user a plan that says "Free" right after they paid.
      for (let attempt = 0; attempt < 5 && !cancelled; attempt++) {
        try {
          const token = tokenStore.get();
          if (!token) return;
          const { data: user } = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (cancelled) return;
          setCurrentUser(user);
          if (user?.plan && user.plan !== 'free') {
            setCheckoutNotice({
              type: 'success',
              text: `You're on the ${user.plan === 'team' ? 'Team' : 'Solo'} plan. Everything is unlocked.`,
            });
            return;
          }
        } catch {
          // fall through to the retry
        }
        await new Promise(r => setTimeout(r, 2000));
      }
      if (!cancelled) {
        setCheckoutNotice({
          type: 'info',
          text: 'Payment received. Your plan will update shortly — refresh if it still shows Free in a minute.',
        });
      }
    };
    confirmPlan();
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  // Auto-dismiss the checkout banner once it settles on a final message.
  useEffect(() => {
    if (!checkoutNotice) return;
    const t = setTimeout(() => setCheckoutNotice(null), 8000);
    return () => clearTimeout(t);
  }, [checkoutNotice]);

  // Cross-tab logout: when tokenStore clears localStorage, log out this tab too.
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'token' && !e.newValue && isAuthenticated) {
        // Token was removed from another tab — mirror the logout here
        setIsAuthenticated(false);
        setCurrentUser(null);
        tokenStore.clear();
        tokenStore.clearRefreshToken();
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [isAuthenticated]);

  // Proactive session keep-alive.
  // Browsers throttle / freeze background tabs, so the access token can quietly
  // expire while the user is on another tab. When they return, the very next
  // API call would 401 and any component using raw fetch() (bypassing the axios
  // interceptor) would silently lose its data — the symptom users report as
  // "the app logs me out after 15-20 min of being on another tab".
  //
  // Fix: refresh proactively (a) every 10 min while focused, and (b) every time
  // the tab becomes visible again. The shared `isRefreshing` flag in the axios
  // interceptor ensures only one refresh is ever in flight.
  useEffect(() => {
    if (!isAuthenticated) return;

    let lastRefresh = Date.now();
    const REFRESH_INTERVAL_MS = 10 * 60 * 1000; // 10 min — well inside the 60-min token lifetime
    const MIN_GAP_MS = 60 * 1000;               // never refresh more than once per minute

    const doRefresh = async (reason) => {
      if (Date.now() - lastRefresh < MIN_GAP_MS) return;
      lastRefresh = Date.now();
      try {
        const fallback = tokenStore.getRefreshToken();
        const resp = await axios.post(
          `${API}/auth/refresh`,
          fallback ? { refresh_token: fallback } : {},
          { withCredentials: true }
        );
        if (resp.data?.access_token) {
          tokenStore.set(resp.data.access_token);
          if (resp.data.refresh_token) tokenStore.setRefreshToken(resp.data.refresh_token);
          devLog(`[KeepAlive] ✓ Refreshed (${reason})`);
        }
      } catch (e) {
        // Don't force a logout here — the response interceptor handles real 401s
        // when the user actually makes a request. A failed keep-alive (e.g.
        // transient network) should NOT eject the user.
        devLog(`[KeepAlive] refresh failed (${reason}):`, e.response?.status || e.message);
      }
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        doRefresh('visibilitychange');
      }
    };

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    const intervalId = setInterval(() => doRefresh('interval'), REFRESH_INTERVAL_MS);

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
      clearInterval(intervalId);
    };
  }, [isAuthenticated]);

  const handleLogin = async (userData = null) => {
    try {
      setNetworkError(false);
      setAuthStage('authenticating');
      devLog("🔑 handleLogin called");
      let user = userData;
      
      if (!user) {
        // If no user data provided, fetch from API using the stored token
        const token = tokenStore.get();
        devLog("🔑 Token from tokenStore:", token ? "exists" : "missing");
        if (!token) {
          throw new Error('No authentication token found');
        }
        
        setAuthStage('loading_profile');
        devLog("🔑 Fetching user data from API...");
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        user = response.data;
      }
      
      if (user) {
        setAuthStage('initializing');
        setCurrentUser(user);
        setIsAuthenticated(true);
        // Land in the app, not back on the marketing homepage.
        setShowAuth(false);
        setEnteredApp(true);
        
        // Defer Socket.IO connection until after first paint (performance optimization)
        const token = tokenStore.get();
        if (token) {
          setTimeout(() => {
            devLog("🔌 Deferred Socket.IO connection (after first paint)...");
            socketService.disconnect();
            socketService.connect(token);
          }, 1000);
        }
        
        setShowWelcome(false);

        // Auto-detect currency from IP on first login (only when not already set)
        if (!user.currency) {
          const detectedCurrency = await detectAndSaveCurrency(tokenStore.get());
          setCurrentUser(prev => prev ? { ...prev, currency: detectedCurrency } : prev);
        }

        // Show onboarding wizard for new users
        if (!user.onboarding_completed) {
          setShowOnboarding(true);
        }
        
        setAuthStage('complete');
        
        // Force a small delay to ensure state updates are processed
        setTimeout(() => setLoading(false), 500);
        
        // Track successful login
        trackLogin('email');
        logEvent('login');
        setUserProperties({
          userId: user.id,
          email: user.email,
          username: user.full_name || user.username,
        });
      } else {
        console.error("❌ No user data available");
      }
    } catch (err) {
      console.error('❌ handleLogin failed:', err);
      
      // Check if it's a network error
      if (err.code === 'ECONNREFUSED' || err.code === 'ERR_NETWORK' || err.message.includes('Network Error')) {
        setNetworkError(true);
      }
      
      // Clear token and reset state
      tokenStore.clear();
      tokenStore.clearRefreshToken();
      localStorage.removeItem('welcomeSeen');
      
      // Disconnect Socket.IO
      if (socketService.isConnected()) {
        socketService.disconnect();
      }
      
      setIsAuthenticated(false);
      setCurrentUser(null);
      setLoading(false);
      console.error('Login failed - state reset');
    }
  };

  const handleWelcomeContinue = () => {
    setShowWelcome(false);
    localStorage.setItem('welcomeSeen', 'true');
  };

  const handleResetPassword = (token) => {
    setResetToken(token);
    setShowForgotPassword(false);
    setShowResetPassword(true);
  };

  const activeTabContent = useMemo(() => {
    switch (activeTab) {
      case ROUTES.TODAY:
        return <PreviewDashboard key={dashboardKey} currentUser={currentUser} setActiveTab={setActiveTab} onOpenIssue={handleOpenIssue} onAskAI={handleAskAI} onOpenMilestones={() => handleOpenBuildView('milestones')} onOpenNewIssue={() => handleOpenBuildView('new-issue')} onDraftIssue={handleDraftIssue} />;
      case ROUTES.BUILD:
        return <PreviewBuildTab currentUser={currentUser} setActiveTab={setActiveTab} onAskAI={handleSolveIssueWithAI} pendingIssueId={pendingIssueId} clearPendingIssue={() => setPendingIssueId(null)} pendingView={pendingBuildView} clearPendingView={() => setPendingBuildView(null)} pendingDraft={pendingIssueDraft} clearPendingDraft={() => setPendingIssueDraft(null)} />;
      case ROUTES.AI:
        // Deliberately NOT wrapped in LockedFeature. AI is metered, not locked:
        // a free user gets 5 generations a day (PLAN_CATALOG.free), and the
        // backend returns 429 + X-Upgrade-Required on the 6th, which the
        // assistant renders as an upgrade prompt. Walling the tab off would
        // mean they never taste the thing we want them to pay for.
        return <AICoFounderAssistant currentUser={currentUser} pendingPrompt={pendingAiPrompt} clearPendingPrompt={() => setPendingAiPrompt(null)} onOpenCreated={handleOpenAICreated} onNavigateTo={handleAINavigate} />;
      case ROUTES.COMPANY:
        return <PreviewCompanyTab currentUser={currentUser} />;
      case ROUTES.TEAM:
        return (
          <LockedFeature
            locked={(currentUser?.effective_plan ?? currentUser?.plan) !== 'team'}
            title="Team Hub is a Team plan feature"
            description="Upgrade to Team to invite teammates, share a group chat, and track team objectives."
            onUpgrade={() => setShowPricing(true)}
          >
            <PreviewTeamHub currentUser={currentUser} />
          </LockedFeature>
        );
      case ROUTES.ADMIN:
        return <AdminDashboard currentUser={currentUser} />;
      default:
        return <PreviewDashboard currentUser={currentUser} setActiveTab={setActiveTab} onOpenIssue={handleOpenIssue} onAskAI={handleAskAI} onOpenMilestones={() => handleOpenBuildView('milestones')} onDraftIssue={handleDraftIssue} />;
    }
  }, [activeTab, currentUser, pendingIssueId, pendingAiPrompt, pendingBuildView, pendingIssueDraft]);

  const renderActiveTab = () => {
    return (
      <Suspense fallback={<LoadingFallback />}>
        {activeTabContent}
      </Suspense>
    );
  };

  // Mobile swipe-to-switch-tabs (left = next, right = previous).
  // Disabled on the AI co-founder tab and while onboarding/legal screens are open.
  const swipeHandlers = useSwipeNavigation({
    tabs: [ROUTES.TODAY, ROUTES.BUILD, ROUTES.COMPANY, ROUTES.TEAM],
    activeTab,
    setActiveTab,
    enabled: isMobile && !showOnboarding && !legalPage && activeTab !== ROUTES.AI,
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--bg-base)' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto mb-4"></div>
          <div className="text-calming">Loading your app...</div>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <ThemeProvider>
      <ToastProvider>
      <ToolHistoryProvider>
      <div className="App">
        <ConsentBanner onShowPrivacy={() => setLegalPage('privacy')} />
        {checkoutNotice && (
          <div
            role="status"
            aria-live="polite"
            style={{
              position: 'fixed',
              top: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 100001,
              maxWidth: 'min(92vw, 440px)',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              padding: '12px 14px',
              borderRadius: 10,
              fontSize: 13,
              lineHeight: 1.45,
              background: 'var(--card-bg-solid, #111820)',
              color: 'var(--text-primary, #e2e8f0)',
              border: `1px solid ${checkoutNotice.type === 'success' ? 'rgba(34,197,94,0.35)' : 'rgba(56,189,248,0.35)'}`,
              boxShadow: '0 12px 32px rgba(0,0,0,0.35)',
            }}
          >
            <span style={{ flex: 1, minWidth: 0 }}>{checkoutNotice.text}</span>
            <button
              type="button"
              onClick={() => setCheckoutNotice(null)}
              aria-label="Dismiss"
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--text-muted, #94a3b8)',
                cursor: 'pointer',
                padding: 0,
                lineHeight: 1,
                fontSize: 16,
              }}
            >
              ×
            </button>
          </div>
        )}
        <ErrorBoundary>
          {isAuthenticated && enteredApp ? (
            <IssuesProvider>
              <TeamProvider>
                  <TimerProvider>
                  <ScheduleProvider>
                    {/* Floating AI bot removed — the AI tab and the Today daily brief are the
                        single AI surface. Notes widget stays mounted (hidden FAB) so the Today
                        card's "Open notes" event still opens the editor. Timer state lives in
                        TimerProvider; controls are surfaced inline on Today. */}
                    <GlobalFloatingNotes />
                    <GlobalNotificationListener onViewIssue={() => setActiveTab(ROUTES.BUILD)} />
                    <FeedbackWidget currentUser={currentUser} currentTab={activeTab} />
                    {showOnboarding && (
                      <OnboardingWizard
                        currentUser={currentUser}
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                        demoTourOpen={showDemoTour}
                        onEmailPrefs={(p) => setCurrentUser(prev => prev ? { ...prev, email_preferences: p } : prev)}
                        onComplete={() => {
                          setShowOnboarding(false);
                          setCurrentUser(prev => prev ? { ...prev, onboarding_completed: true } : prev);
                          setActiveTab(ROUTES.TODAY);
                          setDashboardKey(k => k + 1); // force PreviewDashboard to remount and refetch
                          logEvent('onboarding_complete');
                        }}
                      />
                    )}
                    {showDemoTour && (
                      <DemoModeTour onClose={() => setShowDemoTour(false)} />
                    )}
                {/* Welcome screen removed for better UX */}
              {false ? (
                <ErrorBoundary>
                  <LazyWrapper fallbackName="Welcome Screen">
                    <LazyWelcomeScreen onContinue={handleWelcomeContinue} />
                  </LazyWrapper>
                </ErrorBoundary>
              ) : (
                <div className="flex w-full overflow-x-hidden">
                  {/* Desktop sidebar — skip on mobile where bottom nav is used */}
                  {!isMobile && (
                    <ErrorBoundary>
                      {currentUser && (
                        <CollapsibleSidebar
                          currentUser={currentUser}
                          activeTab={activeTab}
                          setActiveTab={setActiveTab}
                          onShowLegal={setLegalPage}
                          onShowPricing={() => setShowPricing(true)}
                          onProfileUpdate={(updates) => setCurrentUser(prev => ({ ...prev, ...updates }))}
                          showSettings={showSettings}
                          setShowSettings={setShowSettings}
                          onLogout={async () => {
                            try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch {}
                            tokenStore.clear();
                            tokenStore.clearRefreshToken();
                            setIsAuthenticated(false);
                            setCurrentUser(null);
                            window.location.href = '/';
                          }}
                        />
                      )}
                    </ErrorBoundary>
                  )}
                  
                  {/* Main content area - adjust margin based on sidebar */}
                  <main className={`bg-calming flex-1 min-w-0 transition-[margin,padding] duration-200 ${useSidebar ? 'md:pt-14 lg:pt-0 lg:ml-[60px]' : ''} ${isMobile ? (activeTab === ROUTES.AI ? 'overflow-hidden pb-0' : 'min-h-screen pb-20 overflow-x-hidden') : 'min-h-screen'}`} style={isMobile && activeTab === ROUTES.AI ? { height: 'calc(100dvh - 56px)' } : isMobile ? { maxWidth: '100%', width: '100%' } : undefined}
                    {...(isMobile ? swipeHandlers : {})}
                  >
                    <ErrorBoundary>
                      {showPricing ? (
                        <PricingPage onBack={() => setShowPricing(false)} currentUser={currentUser} />
                      ) : legalPage === 'terms' ? (
                        <TermsOfService onBack={() => setLegalPage(null)} />
                      ) : legalPage === 'privacy' ? (
                        <PrivacyPolicy onBack={() => setLegalPage(null)} />
                      ) : legalPage === 'disclaimer' ? (
                        <Disclaimer onBack={() => setLegalPage(null)} />
                      ) : legalPage === 'getstarted' ? (
                        <GetStarted onBack={() => setLegalPage(null)} />
                      ) : (
                        renderActiveTab()
                      )}
                    </ErrorBoundary>
                  </main>

                  {/* Settings panel for mobile (rendered outside sidebar) */}
                  {isMobile && currentUser && (
                    <SidebarSettings
                      isOpen={showSettings}
                      onClose={() => setShowSettings(false)}
                      currentUser={currentUser}
                      onLogout={async () => {
                        try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch {}
                        tokenStore.clear();
                        tokenStore.clearRefreshToken();
                        setIsAuthenticated(false);
                        setCurrentUser(null);
                        window.location.href = '/';
                      }}
                      onShowLegal={setLegalPage}
                      onShowPricing={() => setShowPricing(true)}
                      onProfileUpdate={(updates) => setCurrentUser(prev => ({ ...prev, ...updates }))}
                    />
                  )}
                  {/* Mobile settings entry — top-right gear, replaces the bottom-nav Settings slot now used by AI */}
                  {isMobile && currentUser && !legalPage && !showOnboarding && activeTab !== ROUTES.AI && (
                    <button
                      type="button"
                      aria-label="Settings"
                      data-fp-fab="settings"
                      onClick={() => setShowSettings(true)}
                      style={{
                        position: 'fixed',
                        bottom: 'calc(48px + env(safe-area-inset-bottom, 0px) + 66px)',
                        left: 16,
                        zIndex: 9998,
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        border: '1px solid var(--card-border, rgba(255,255,255,0.08))',
                        background: 'var(--card-bg-solid, rgba(20,22,28,0.7))',
                        color: 'var(--text-primary, #e5e7eb)',
                        backdropFilter: 'saturate(180%) blur(18px)',
                        WebkitBackdropFilter: 'saturate(180%) blur(18px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 16,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
                        cursor: 'pointer',
                      }}
                    >
                      ⚙
                    </button>
                  )}
                </div>
              )}
              
              {/* Mobile Bottom Navigation - Always visible on all tabs */}
              {isMobile && currentUser && isAuthenticated && (
                <MobileBottomNav 
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  onShowSettings={() => setShowSettings(true)}
                />
              )}
                  </ScheduleProvider>
                  </TimerProvider>
              </TeamProvider>
            </IssuesProvider>
          ) : (
            <ErrorBoundary>
              <>
                {legalPage === 'terms' ? (
                  <TermsOfService onBack={() => setLegalPage(null)} />
                ) : legalPage === 'privacy' ? (
                  <PrivacyPolicy onBack={() => setLegalPage(null)} />
                ) : legalPage === 'disclaimer' ? (
                  <Disclaimer onBack={() => setLegalPage(null)} />
                ) : legalPage === 'getstarted' ? (
                  <GetStarted onBack={() => setLegalPage(null)} />
                ) : showForgotPassword ? (
                  <ForgotPasswordForm
                    onBack={closeForgotPassword}
                    onResetPassword={handleResetPassword}
                  />
                ) : showResetPassword ? (
                  <ResetPasswordForm
                    token={resetToken}
                    onSuccess={() => {
                      setShowResetPassword(false);
                      setShowForgotPassword(false);
                      setShowRegister(false);
                      setResetToken("");
                    }}
                  />
                ) : showAuth && showRegister ? (
                  <RegisterForm
                    onLogin={handleLogin}
                    switchToLogin={() => setShowRegister(false)}
                    onShowLegal={setLegalPage}
                  />
                ) : showAuth ? (
                  <LoginForm
                    onLogin={handleLogin}
                    switchToRegister={() => setShowRegister(true)}
                    switchToForgotPassword={openForgotPassword}
                    onShowLegal={setLegalPage}
                  />
                ) : (
                  <LandingPage
                    isAuthenticated={isAuthenticated}
                    onSignIn={() => openAuth(false)}
                    onSignUp={() => openAuth(true)}
                    onEnterApp={enterApp}
                    onShowLegal={setLegalPage}
                  />
                )}
              </>
            </ErrorBoundary>
          )}
        </ErrorBoundary>
      </div>
      </ToolHistoryProvider>
      </ToastProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
};

export default App;
